import { Link } from "gatsby"
import { useState } from "react"
import { Helmet } from "react-helmet"
import Header from "../../components/Header"
import Layout from "../../components/Layout"
import { btn, btnDark, btnPrimary } from "../../ds/button.module.css"
import { WhiteArrowRight } from "../../ds/Shapes"
import creativeEditorImg from "../../images/attendees-poster-creation.png"
import heroImg from "../../images/compare/hero-linkedin-feed.png"
import premagicFeedImg from "../../images/compare/premagic-feed-test.jpg"
import ingoFeedImg from "../../images/compare/ingo-feed-test.jpg"
import useWindowDimensions from "../../utils/useWindowDimensions"

/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */

const features = [
  {
    capability: "Social advocacy posters",
    premagic:
      "Full drag-and-drop editor. Any layout, any shape, full brand control.",
    ingo: "Fixed template. Fill in a form, choose font color.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Mobile-first content",
    premagic:
      "Vertical layouts for LinkedIn/Instagram mobile feeds (68% of usage).",
    ingo: "Landscape format. Designed for desktop. Small on mobile.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Email campaigns",
    premagic: "Fully branded, end-to-end visual customization.",
    ingo: "Locked template. Text-only customization.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "AI photo distribution",
    premagic: "99.9% facial recognition. Instant delivery via WhatsApp/email.",
    ingo: "Not available.",
    premagicHas: true,
    ingoHas: false,
  },
  {
    capability: "AI avatars",
    premagic: "Branded avatar studio. 50% of attendees share on social.",
    ingo: "Not available.",
    premagicHas: true,
    ingoHas: false,
  },
  {
    capability: "Multi-language support",
    premagic: "Multiple languages including Arabic. Attendee chooses.",
    ingo: "English only.",
    premagicHas: true,
    ingoHas: false,
  },
  {
    capability: "Advocate-level reporting",
    premagic:
      "See who posted what, when. Track referrals, revenue per advocate.",
    ingo: "Aggregate metrics. No post-level verification.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Creative editor",
    premagic: "Canva-like drag-and-drop. Move, resize, reimagine any element.",
    ingo: "Form-based. Components locked in place.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "User experience",
    premagic: "Modern design-tool interface. Real-time preview.",
    ingo: "Legacy UI. Form-based workflow.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Execution support",
    premagic:
      "Parallel team. We design campaigns, optimize, support on-ground.",
    ingo: "Software vendor. Execution stays with your team.",
    premagicHas: true,
    ingoHas: false,
  },
  {
    capability: "Live support",
    premagic: "Dedicated WhatsApp group. On-demand local + face-to-face.",
    ingo: "Email-only. Remote and asynchronous.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Post types",
    premagic:
      "Attending, speaker shoutouts, sponsor highlights, photos, avatars.",
    ingo: "'I'm attending' social posts.",
    premagicHas: true,
    ingoHas: true,
  },
  {
    capability: "Channels",
    premagic: "LinkedIn, email, WhatsApp, download, embed.",
    ingo: "Primarily LinkedIn.",
    premagicHas: true,
    ingoHas: true,
  },
]

const lifecyclePhases = [
  {
    phase: "Before the Event",
    color: "#7251F4",
    premagic:
      "Referral posters and hyper-personalized email campaigns with full visual control.",
    ingo: "Referral posts with locked email templates.",
  },
  {
    phase: "During the Event",
    color: "#FF6550",
    premagic:
      "Captures photos, distributes instantly via facial recognition, powers branded galleries, integrates sponsor content.",
    ingo: "No during-event capability.",
  },
  {
    phase: "After the Event",
    color: "#61F2C2",
    premagic:
      "Photo distribution generates thousands of organic shares. Branded content creates a long tail of social proof.",
    ingo: "Advocacy ends when the event starts.",
  },
]

const onlyPremagicFeatures = [
  {
    title: "AI Photo Distribution",
    description:
      "Every attendee gets their event photos delivered instantly. They share because it's their content.",
  },
  {
    title: "Avatar Studio",
    description:
      "Branded AI avatars. 3x more engagement than traditional activations.",
  },
  {
    title: "Animated Creatives",
    description: "Motion content that stops the scroll.",
  },
  {
    title: "Automated Re-Targeting",
    description: "Nudge attendees who haven't shared yet at the right moment.",
  },
  {
    title: "Multi-Language Campaigns",
    description: "Run the same event in Arabic and English simultaneously.",
  },
]

const caseStudies = [
  {
    name: "GITEX Global 2025",
    bgColor: "#E6DFFF",
    stats: [
      { value: "200,000", label: "Attendees" },
      { value: "16,185", label: "Advocacy posts" },
      { value: "$219K", label: "Ad spend saved" },
    ],
    cta: "/case-study/gitex-global",
  },
  {
    name: "Khaleej Times Events",
    bgColor: "#ffccc5",
    stats: [
      { value: "700", label: "Referrals from posters" },
      { value: "$4,596", label: "Measurable ROI" },
      { value: "80", label: "Active advocates" },
    ],
    cta: "/case-study/khaleejtimes",
  },
]

const comparisonFaqs = [
  {
    question: "We're already using InGo. How hard is it to switch?",
    answer:
      "Premagic is a self-serve platform \u2014 you can set up an event and launch a campaign in 15 minutes, just like InGo. We also have a library of premade templates if you want to get started quickly or don't have a dedicated design team. And if you'd rather have our team handle end-to-end execution \u2014 including design and deploying campaigns \u2014 we do that too.",
  },
  {
    question: "Does Premagic integrate with our event registration platform?",
    answer:
      "Yes. Premagic integrates with major registration and event management platforms. Contact us for specifics on your stack.",
  },
  {
    question:
      "We only care about LinkedIn advocacy. Do we really need photos and avatars?",
    answer:
      "Photos and avatars are LinkedIn advocacy. When an attendee posts their AI avatar or a photo from your event, that's a branded post on LinkedIn \u2014 just a far more engaging one. A feed full of identical 'I'm attending' posters becomes wallpaper. Mix in personalized photos, custom avatars, and speaker shoutouts and suddenly your event owns the feed instead of blending into it. More content variety means more stops, more clicks, more reach.",
  },
  {
    question: "How does pricing compare?",
    answer:
      "Premagic uses usage-based pricing, so you pay for what you actually use. For smaller events, you don't burn your budget on an annual contract you can't justify. For larger events, you get significantly more capability per dollar \u2014 including execution support, photo distribution, and AI features.",
  },
  {
    question: "Can Premagic support global events with multiple languages?",
    answer:
      "Yes. Design creatives in multiple languages including Arabic. Attendees choose their preferred language. InGo currently supports English only.",
  },
]

const awards = [
  "Event Tech Awards 2025 \u2014 Best Use of Technology for Marketing an Event",
  "WOW Awards Gold \u2014 Innovative Use of Event Tech",
  "G2 Leader Winter 2026",
  "G2 Momentum Leader",
]

/* ------------------------------------------------------------------ */
/*  INLINE COMPONENTS                                                  */
/* ------------------------------------------------------------------ */

function CheckIcon({ yes }: { yes: boolean }) {
  if (yes) {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#7251F4]/10 text-[#7251F4] text-xs font-bold mr-2 flex-shrink-0">
        &#10003;
      </span>
    )
  }
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-50 text-red-400 text-xs font-bold mr-2 flex-shrink-0">
      &#10005;
    </span>
  )
}

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const toggle = (index: number) =>
    setOpenIndex(current => (current === index ? null : index))

  return (
    <section className="bg-[#fff7f4] py-16 px-4 md:px-0">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-center text-2xl md:text-3xl font-bold text-[#222222] mb-10">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {comparisonFaqs.map((item, index) => {
            const isOpen = openIndex === index
            return (
              <div
                key={item.question}
                className="rounded-2xl bg-[#ffe7dd] shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="font-semibold text-[15px] md:text-base text-[#2a2530]">
                    {item.question}
                  </span>
                  <span className="h-4 w-4 p-2 ml-4 flex items-center justify-center rounded-full bg-white text-sm font-bold text-[#51414b]">
                    {isOpen ? "\u2212" : "+"}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 py-2"
                      : "grid-rows-[0fr] opacity-0"
                  } border-t border-[#f4cfc1]`}
                >
                  <div className="overflow-hidden px-5 text-sm md:text-[15px] text-[#4d4048]">
                    {item.answer}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  FEATURE TABLE (responsive)                                         */
/* ------------------------------------------------------------------ */

function FeatureTableDesktop() {
  return (
    <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-100">
      {/* Header */}
      <div className="grid grid-cols-[1fr_1fr_1fr] bg-[#F7F5FF] px-6 py-4">
        <span className="font-semibold text-sm text-gray-700">Capability</span>
        <span className="flex justify-center">
          <span className="bg-[#7251F4] text-white px-5 py-1.5 rounded-lg text-sm font-semibold">
            Premagic
          </span>
        </span>
        <span className="flex justify-center">
          <span className="bg-gray-200 text-gray-600 px-5 py-1.5 rounded-lg text-sm font-semibold">
            InGo
          </span>
        </span>
      </div>
      {/* Rows */}
      {features.map((f, idx) => (
        <div
          key={idx}
          className="grid grid-cols-[1fr_1fr_1fr] px-6 py-4 border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
        >
          <span className="font-medium text-sm text-black pr-4">
            {f.capability}
          </span>
          <span className="flex items-start text-sm text-gray-700 px-2">
            <CheckIcon yes={f.premagicHas} />
            <span>{f.premagic}</span>
          </span>
          <span className="flex items-start text-sm text-gray-500 px-2">
            <CheckIcon yes={f.ingoHas} />
            <span>{f.ingo}</span>
          </span>
        </div>
      ))}
    </div>
  )
}

function FeatureTableMobile() {
  return (
    <div className="md:hidden space-y-4">
      {features.map((f, idx) => (
        <div
          key={idx}
          className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
        >
          <h4 className="font-semibold text-sm text-black mb-3">
            {f.capability}
          </h4>
          <div className="mb-2">
            <span className="inline-block bg-[#7251F4] text-white text-xs px-3 py-1 rounded-md font-medium mb-1">
              Premagic
            </span>
            <p className="text-sm text-gray-700 mt-1 flex items-start">
              <CheckIcon yes={f.premagicHas} />
              <span>{f.premagic}</span>
            </p>
          </div>
          <div>
            <span className="inline-block bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-md font-medium mb-1">
              InGo
            </span>
            <p className="text-sm text-gray-500 mt-1 flex items-start">
              <CheckIcon yes={f.ingoHas} />
              <span>{f.ingo}</span>
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */

export default function IngoVsPremagic() {
  const { width } = useWindowDimensions()
  const isMobileUp = width >= 768

  return (
    <Layout
      title="Premagic vs InGo: Full-Lifecycle Event Marketing Comparison"
      description="Compare Premagic and InGo side by side. See why 4,000+ events choose Premagic for creative control, AI-powered features, and full-lifecycle event marketing."
      keywords={[
        "premagic vs ingo",
        "ingo alternative",
        "event marketing comparison",
        "event advocacy platform",
      ]}
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Compare", url: "/compare/" },
        { name: "Premagic vs InGo", url: "/compare/ingo-vs-premagic/" },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: comparisonFaqs.map(item => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          })}
        </script>
      </Helmet>
      {/* ============================================================ */}
      {/* S1 — HERO                                                     */}
      {/* ============================================================ */}
      <div style={{ backgroundColor: "#7251F4" }}>
        <Header isWhiteLogo />
      </div>

      <section
        className="py-10 md:py-16"
        style={{ backgroundColor: "#7251F4" }}
      >
        <div className="px-5 md:px-0 mx-auto md:w-[1080px] relative overflow-hidden">
          <div className="md:flex items-center relative z-10">
            <div className="md:w-2/3 flex flex-col items-center md:items-start justify-center md:text-left text-center">
              <span className="text-sm uppercase tracking-widest text-white/60 mb-4">
                Premagic vs InGo
              </span>

              <h1 className="text-3xl md:text-5xl font-bold text-white !leading-[1.3em] mb-6">
                Both platforms turn attendees into advocates. One gives you full
                creative control over how.
              </h1>

              <p className="text-white/90 text-base md:text-lg md:w-4/5 mb-6">
                When you hand someone a stunning, personalized, mobile-optimized
                poster with their name and photo on it &mdash; or an
                AI-generated avatar that makes them look incredible &mdash; they
                don&rsquo;t need a nudge. They <em>want</em> to post it. Event
                marketing has moved on from templated landscape banners and
                form-based editors. Your advocacy platform should have too.
              </p>

              <div className="flex flex-wrap gap-4 items-center">
                <Link
                  to="/request-a-demo/"
                  className={`${btn} ${btnDark} text-xs relative w-max group`}
                >
                  <span className="mr-4 ml-2">Request a Demo</span>
                  <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
                </Link>
                <a
                  href="#feature-comparison"
                  className="text-white underline underline-offset-4 text-sm hover:text-white/80 transition-colors"
                >
                  See the Feature Comparison &darr;
                </a>
              </div>

              {/* Stat boxes */}
              <div className="grid max-w-2xl grid-cols-3 gap-4 mt-10 w-full">
                {[
                  { value: "4,000+", label: "Events powered monthly" },
                  { value: "4x", label: "More registrations" },
                  { value: "$160K+", label: "Ad spend saved at GITEX" },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="rounded-2xl bg-[#1c0c4b]/60 px-3 md:px-8 py-4 md:py-6 text-white text-center shadow-lg shadow-black/20 animate-fadeUp opacity-0"
                    style={{ animationDelay: `${0.1 + i * 0.2}s` }}
                  >
                    <div className="text-xl md:text-3xl font-semibold leading-none">
                      {stat.value}
                    </div>
                    <p className="mt-2 text-xs md:text-sm text-white/80">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Trust */}
              <p className="text-white/40 text-xs mt-8 tracking-wide">
                Trusted by GITEX Global &middot; Cityscape Global &middot; AI
                Everything &middot; Event Tech Awards 2025 Winner
              </p>
            </div>

            <div className="hidden md:block absolute right-[-80px] top-1/2 -translate-y-1/2 w-[540px]">
              <img
                src={heroImg}
                alt="LinkedIn feeds powered by Premagic — avatars, posters, and event photos driving organic engagement"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S2 — QUICK VERDICT                                            */}
      {/* ============================================================ */}
      <section className="py-16 md:py-20 px-5">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-700 text-base md:text-lg mb-6">
            <strong className="text-black">InGo</strong> is a referral marketing
            tool built for B2B events. It automates social sharing through
            one-click posts and email campaigns, and it&rsquo;s trusted by
            enterprise organizers.
          </p>
          <p className="text-gray-700 text-base md:text-lg mb-6">
            <strong className="text-black">Premagic</strong> does everything
            InGo does &mdash; and extends into full-lifecycle event marketing.
            You get a Canva-like design editor for branded posters, mobile-first
            layouts built for how LinkedIn actually works on phones, AI photo
            distribution, avatar generation, multi-language support, and a team
            that executes alongside you.
          </p>
          <p className="text-black text-base md:text-lg font-medium">
            <strong>The core difference:</strong>{" "}
            <span className="relative inline-block">
              <span
                className="block absolute left-0 right-0 h-1.5 rounded-xl bottom-0.5 z-0"
                style={{ backgroundColor: "#61F2C2" }}
              />
              <span className="relative z-10">
                InGo was built for a desktop-first world
              </span>
            </span>{" "}
            of landscape banners and email blasts. Premagic was built for how
            people actually consume and share content today &mdash; vertical,
            visual, mobile, instant.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S3 — FEATURE COMPARISON TABLE                                 */}
      {/* ============================================================ */}
      <section id="feature-comparison" className="py-16 md:py-20 px-5 md:px-0">
        <div className="mx-auto md:w-[1080px]">
          <h2 className="text-2xl md:text-4xl font-bold text-black text-center mb-4">
            Feature-by-Feature Comparison
          </h2>
          <p className="text-gray-500 text-center mb-12 text-base md:text-lg max-w-2xl mx-auto">
            A detailed look at capabilities across the event marketing
            lifecycle.
          </p>
          <FeatureTableDesktop />
          <FeatureTableMobile />
        </div>
      </section>

      {/* ============================================================ */}
      {/* S4 — THE FEED TEST                                            */}
      {/* ============================================================ */}
      <section
        className="py-16 md:py-20 px-5"
        style={{ backgroundColor: "#7251F4" }}
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-4">
            The Feed Test: 2012 vs. 2026
          </h2>
          <p className="text-white/70 text-center mb-12 italic text-sm md:text-base max-w-2xl mx-auto">
            Here&rsquo;s what the same event looks like on LinkedIn depending on
            which platform powers it.
          </p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            {/* Premagic */}
            <div className="bg-white/10 rounded-2xl p-6 md:p-8">
              <h3 className="text-white font-bold text-xl mb-4">
                With Premagic
              </h3>
              <p className="text-white/90 text-sm md:text-base mb-6">
                A vertical, branded poster fills the entire mobile screen. The
                attendee&rsquo;s photo is prominent. The design is unique to
                your event. It stops the scroll because it looks like{" "}
                <em>content</em>, not an automated post.
              </p>
              <img
                src={premagicFeedImg}
                alt="Premagic-powered LinkedIn post — Watch My Session Video, vertical format filling the mobile screen"
                className="rounded-xl w-full max-w-xs mx-auto"
              />
            </div>

            {/* InGo */}
            <div className="bg-white/5 rounded-2xl p-6 md:p-8">
              <h3 className="text-white/80 font-bold text-xl mb-4">
                With InGo
              </h3>
              <p className="text-white/70 text-sm md:text-base mb-6">
                A landscape banner appears thumbnail-sized on mobile. The
                template is recognizable &mdash; every InGo event produces the
                same visual signature. It gets scrolled past because it looks
                exactly like what it is: a 2010-era referral widget.
              </p>
              <img
                src={ingoFeedImg}
                alt="InGo-powered LinkedIn post — landscape I'm Attending banner, small on mobile feeds"
                className="rounded-xl w-full max-w-xs mx-auto"
              />
            </div>
          </div>

          <p className="text-white/50 text-sm italic text-center mt-10">
            68% of LinkedIn is mobile. Format isn&rsquo;t a design detail.
            It&rsquo;s the difference between seen and invisible.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S5 — THE CREATIVE FREEDOM GAP                                 */}
      {/* ============================================================ */}
      <section
        className="py-16 md:py-20 px-5 md:px-0"
        style={{ backgroundColor: "#F7F5FF" }}
      >
        <div className="mx-auto md:w-[1080px]">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Image placeholder */}
            <div className="w-full md:w-1/2">
              <img
                src={creativeEditorImg}
                alt="Premagic poster editor — make it pop in your brand colors with photo widgets, auto event details, and custom branding"
                className="w-full max-w-md mx-auto"
              />
            </div>

            {/* Text */}
            <div className="w-full md:w-1/2">
              <h2 className="text-2xl md:text-3xl font-bold text-black leading-snug mb-4">
                The Creative Freedom Gap
              </h2>
              <p className="italic text-gray-500 mb-6">
                Your event isn&rsquo;t generic. Your advocacy content
                shouldn&rsquo;t be either.
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4">
                Premagic gives you a Canva-like editor. Place photos, text,
                shapes, logos anywhere you want. Create designs that are
                unmistakably <em>yours</em>. Every element is movable,
                resizable, reimaginable.
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4">
                InGo gives you a form. Type your event name. Upload a logo.
                Choose a font color. The structure is locked. Every InGo-powered
                event ends up looking like every other InGo-powered event.
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4">
                Open both editors side by side and the era gap is immediate.
                Premagic feels like a modern design tool &mdash; drag, drop,
                preview in real-time. InGo feels like a registration form from
                2012. The interface hasn&rsquo;t kept pace with how event brands
                think about content today.
              </p>
              <p className="text-black font-medium text-sm md:text-base">
                Your attendees notice. A polished, custom-designed poster gets
                shared. A generic, template-stamped graphic gets ignored.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S6 — THE LIFECYCLE GAP                                        */}
      {/* ============================================================ */}
      <section className="py-16 md:py-20 px-5 md:px-0">
        <div className="mx-auto md:w-[1080px]">
          <h2 className="text-2xl md:text-4xl font-bold text-black text-center mb-4">
            The Lifecycle Gap
          </h2>
          <p className="text-gray-500 text-center mb-12 italic text-sm md:text-base max-w-2xl mx-auto">
            InGo focuses on one moment: the pre-event social post. What about
            everything else?
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {lifecyclePhases.map((phase, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border-t-4"
                style={{ borderColor: phase.color }}
              >
                <h3 className="font-bold text-lg text-black mb-4">
                  {phase.phase}
                </h3>
                <div className="mb-4">
                  <span className="inline-block bg-[#7251F4] text-white text-xs px-3 py-1 rounded-md font-medium mb-2">
                    Premagic
                  </span>
                  <p className="text-sm text-gray-700">{phase.premagic}</p>
                </div>
                <div>
                  <span className="inline-block bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-md font-medium mb-2">
                    InGo
                  </span>
                  <p className="text-sm text-gray-400">{phase.ingo}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-2xl mx-auto mt-10">
            <p className="text-gray-600 italic text-center text-sm md:text-base">
              The event doesn&rsquo;t stop when doors open. Neither should your
              marketing.
            </p>
            <p className="text-gray-500 italic text-center text-sm mt-4">
              InGo was designed when event marketing meant &ldquo;get people to
              register and hope they tweet about it.&rdquo; The industry has
              evolved. The tools should too.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S7 — THE EXECUTION GAP                                        */}
      {/* ============================================================ */}
      <section
        className="py-16 md:py-20 px-5 md:px-0"
        style={{ backgroundColor: "#1F0F61" }}
      >
        <div className="mx-auto md:w-[1080px]">
          <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
            {/* Text */}
            <div className="w-full md:w-1/2">
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-snug mb-4">
                The Execution Gap
              </h2>
              <p className="italic text-white/60 mb-6 text-sm md:text-base">
                The best software in the world doesn&rsquo;t help if your team
                is underwater two days before the event.
              </p>
              <p className="text-white/80 text-sm md:text-base mb-4">
                Premagic doesn&rsquo;t just give you a platform. The team
                designs campaigns in your account. Works alongside your team
                from launch to post-event. Optimizes creatives in real-time.
                Shows up on-ground when needed. You get a WhatsApp group with
                dedicated support &mdash; not an email queue with a 24-hour SLA.
              </p>
              <p className="text-white/80 text-sm md:text-base mb-4">
                InGo is a software vendor. A good one. But execution, design,
                optimization &mdash; that&rsquo;s on your team.
              </p>
              <p className="text-white font-medium text-sm md:text-base">
                For events running at GITEX scale (200,000 attendees), this
                distinction isn&rsquo;t a nice-to-have. It&rsquo;s the
                difference between success and chaos.
              </p>
            </div>

            {/* Testimonial card */}
            <div className="w-full md:w-1/2">
              <div
                className="bg-white rounded-2xl p-6 md:p-8 shadow-xl"
                style={{ borderLeft: "4px solid #7251F4" }}
              >
                <p className="text-gray-700 text-sm md:text-base italic mb-6 leading-relaxed">
                  &ldquo;I wanted to express my gratitude for the tremendous job
                  your team did at GITEX. After unexpected issues, your team
                  stepped up and provided outstanding support. The quick
                  pointers your team gave us enabled us to get things back on
                  track smoothly. By day two, we were much more comfortable,
                  knowing that we have your team tagging with us.&rdquo;
                </p>
                <div>
                  <p className="font-bold text-black text-sm">Vinod Nalawade</p>
                  <p className="text-gray-500 text-xs">GITEX Global</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S8 — PREMAGIC-ONLY FEATURES                                   */}
      {/* ============================================================ */}
      <section className="py-16 md:py-20 px-5 md:px-0">
        <div className="mx-auto md:w-[1080px]">
          <h2 className="text-2xl md:text-4xl font-bold text-black text-center mb-4">
            What Premagic Does That InGo Doesn&rsquo;t
          </h2>
          <p className="text-gray-500 text-center mb-12 text-base">
            Capabilities that exist only on Premagic.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {onlyPremagicFeatures.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm"
                style={{ borderBottom: "4px solid #7251F4" }}
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S9 — REAL RESULTS                                             */}
      {/* ============================================================ */}
      <section className="py-16 md:py-20 px-5 md:px-0">
        <div className="mx-auto md:w-[1080px]">
          <h2 className="text-2xl md:text-4xl font-bold text-black text-center mb-12">
            What Full-Lifecycle Event Marketing Delivers
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((cs, idx) => (
              <div
                key={idx}
                className="rounded-2xl p-6 md:p-8"
                style={{ backgroundColor: cs.bgColor }}
              >
                <h3 className="text-xl md:text-2xl font-bold text-black mb-6">
                  {cs.name}
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {cs.stats.map((stat, sidx) => (
                    <div
                      key={sidx}
                      className="bg-white rounded-xl px-3 py-4 text-center shadow-sm"
                    >
                      <div className="text-lg md:text-xl font-bold text-[#1F0F61]">
                        {stat.value}
                      </div>
                      <p className="text-xs text-[#7251F4] mt-1">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
                <Link
                  to={cs.cta}
                  className={`${btn} ${btnPrimary} text-xs w-max group flex items-center`}
                >
                  <span className="mr-3">Read the Case Study</span>
                  <WhiteArrowRight className="transition-transform duration-300 group-hover:translate-x-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S10 — EMV CALCULATOR + AWARDS                                 */}
      {/* ============================================================ */}
      <section
        className="py-16 md:py-20 px-5"
        style={{ backgroundColor: "#F7F5FF" }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-black mb-4">
            See What Your Event&rsquo;s Social Reach Is Worth
          </h2>
          <p className="text-gray-500 text-base md:text-lg mb-8">
            Calculate the earned media value your event could generate with
            advocate-powered marketing.
          </p>
          <Link
            to="/reports/emv-calculator/"
            className={`${btn} ${btnPrimary} text-xs relative w-max group mx-auto`}
          >
            <span className="mr-4 ml-2">Calculate Your Event&rsquo;s EMV</span>
            <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
          </Link>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mt-12">
            {awards.map((award, idx) => (
              <span
                key={idx}
                className="bg-white rounded-xl px-4 py-3 text-xs md:text-sm font-medium text-gray-700 shadow-sm"
              >
                {award}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S11 — FAQ                                                     */}
      {/* ============================================================ */}
      <FaqAccordion />

      {/* ============================================================ */}
      {/* S12 — FINAL CTA                                               */}
      {/* ============================================================ */}
      <section
        className="py-16 md:py-20 px-5"
        style={{ backgroundColor: "#7251F4" }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to See the Difference?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8">
            Join 4,000+ events using Premagic to turn every attendee into a
            marketer.
          </p>
          <Link
            to="/request-a-demo/"
            className={`${btn} ${btnDark} text-xs relative w-max group mx-auto`}
          >
            <span className="mr-4 ml-2">Request a Demo</span>
            <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
          </Link>
        </div>
      </section>
    </Layout>
  )
}
